import { apiClient } from '@/lib/api/client'

export interface TwoFactorSettings {
  enabled: boolean
  required: boolean
  backup_codes_count: number
}

export interface SecuritySettings {
  session_timeout: number
  max_login_attempts: number
  password_min_length: number
  password_require_special_chars: boolean
  password_require_numbers: boolean
}

export interface SecuritySettingsData {
  two_factor: {
    enabled: boolean
    required: boolean
    backup_codes_count: number
  }
  session: {
    session_enabled: boolean
    session_timeout: number
    max_login_attempts: number
    lockout_duration: number
  }
}

export interface GeneralSettingsData {
  site: { auth_logo?: string; sidenav_logo?: string }
  date_time: {
    timezone: string
    date_format: string
    time_format: string
  }
  language: {
    default_language: string
  }
}

export interface EmailSettings {
  mailer: string
  mail_from_name: string
  mail_from_address: string
  smtp?: {
    host: string
    port: string
    encryption: string
    username: string
    password: string
  }
  mailgun?: {
    domain: string
    secret: string
  }
  postmark?: {
    token: string
  }
  ses?: {
    key: string
    secret: string
    region: string
  }
  microsoft?: {
    tenant_id: string
    client_id: string
    client_secret: string
    sender_email: string
  }
}

export interface EmailSettingsData {
  mailer: string
  mail_from_name: string
  mail_from_address: string
  smtp: {
    host: string
    port: string
    encryption: string
    username: string
    password: string
  }
  mailgun: {
    domain: string
    secret: string
  }
  postmark: {
    token: string
  }
  ses: {
    key: string
    secret: string
    region: string
  }
  microsoft: {
    tenant_id: string
    client_id: string
    client_secret: string
    sender_email: string
  }
}

export interface UISettings {
  items_per_page: number
  theme_color: string
  sidebar_collapsed: boolean
}

export interface GeneralSettings {
  site: {
    auth_logo?: string | File | null
    sidenav_logo?: string | File | null
  }
  date_time: {
    timezone: string
    date_format: string
    time_format: string
  }
  language: {
    default_language: string
  }
}

export interface SystemSettings {
  two_factor: TwoFactorSettings
  security: SecuritySettings
  general: GeneralSettingsData
  email: EmailSettings
  ui: UISettings
}

export interface TwoFactorStatus {
  isEnabled: boolean
  isRequired: boolean
  hasBackupCodes: boolean
  lastUsedAt?: string
}

export const SettingsService = {
  async getSettings(): Promise<{ success: boolean; data: SystemSettings }> {
    try {
      const response = await apiClient.get('/system-settings/settings')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch settings')
    }
  },

  async updateSettings(settings: Partial<SystemSettings>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/system-settings/settings', settings)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update settings')
    }
  },

  async getOption(key: string): Promise<{ success: boolean; data: { key: string; value: any } }> {
    try {
      const response = await apiClient.get(`/system-settings/settings/${key}`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch option')
    }
  },

  async updateOption(key: string, value: any, type: string = 'string', description?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.put(`/system-settings/settings/${key}`, {
        value,
        type,
        description
      })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update option')
    }
  },

  async getTwoFactorStatus(): Promise<{ success: boolean; data: TwoFactorStatus }> {
    try {
      const response = await apiClient.get('/system-settings/settings/two-factor/status')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch 2FA status')
    }
  },

  async enableTwoFactor(): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response = await apiClient.post('/system-settings/settings/two-factor/enable')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to enable 2FA')
    }
  },

  async disableTwoFactor(): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response = await apiClient.post('/system-settings/settings/two-factor/disable')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to disable 2FA')
    }
  },

  async generateBackupCodes(): Promise<{ success: boolean; message: string; data: { backupCodes: string[] } }> {
    try {
      const response = await apiClient.post('/system-settings/settings/two-factor/backup-codes')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to generate backup codes')
    }
  },

  async initializeDefaults(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/system-settings/settings/initialize')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to initialize defaults')
    }
  },

  async getGeneralSettings(): Promise<{ success: boolean; data: GeneralSettingsData }> {
    try {
      const response = await apiClient.get('/system-settings/general')
      return { success: true, data: response.data.data || response.data }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch general settings')
    }
  },

  async updateGeneralSettings(settings: Partial<GeneralSettingsData>): Promise<{ success: boolean; message: string }> {
    try {
      const settingsData: any = { ...settings }
      // Remove empty site object if it exists
      if (settingsData.site && Object.keys(settingsData.site).length === 0) {
        delete settingsData.site
      }
      const response = await apiClient.put('/system-settings/general', settingsData)
      return { success: true, message: response.data.message || 'General settings updated successfully' }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data
        const customError: any = new Error(errorData.message || 'Failed to update general settings')
        customError.response = { data: errorData }
        throw customError
      }
      throw new Error(error.message || 'Failed to update general settings')
    }
  },

  async getSecuritySettings(): Promise<{ success: boolean; data: SecuritySettingsData }> {
    try {
      const response = await apiClient.get('/system-settings/security')
      return { success: true, data: response.data.data || response.data }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch security settings')
    }
  },

  async updateSecuritySettings(settings: Partial<SecuritySettingsData>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.put('/system-settings/security', settings)
      return { success: true, message: response.data.message || 'Security settings updated successfully' }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update security settings')
    }
  },

  async getEmailSettings(): Promise<{ success: boolean; data: EmailSettingsData }> {
    try {
      const response = await apiClient.get('/system-settings/email-config')
      return { success: true, data: response.data.data || response.data }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch email settings')
    }
  },

  async updateEmailSettings(settings: Partial<EmailSettingsData>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.put('/system-settings/email-config', settings)
      return { success: true, message: response.data.message || 'Email settings updated successfully' }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data
        const customError: any = new Error(errorData.message || 'Failed to update email settings')
        customError.response = { data: errorData }
        throw customError
      }
      throw new Error(error.message || 'Failed to update email settings')
    }
  }
}

